import { Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';

/**
 * Styled button component with fully rounded (pill-shaped) design
 * Matches the rounded EllaFin logo aesthetic.
 * Uses MUI theme for all colors so buttons adapt to theme switching.
 *
 * Supports variants:
 * - contained: Solid primary background (default CTA style)
 * - outlined: Primary border with primary text
 * - text/ghost: No border, primary text, secondary text on hover
 * - link: Similar to ghost but for navigation
 *
 * @param {Object} props - Button props
 * @param {string} [props.variant] - Button variant (contained, outlined, text, ghost, link)
 * @param {string} [props.size] - Button size (small, medium, large)
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} [props.onClick] - Click handler
 * @param {Object} [props.sx] - Additional MUI sx props
 * @param {boolean} [props.fullWidth] - Whether button should be full width
 * @param {string} [props.color] - Button color override (defaults to theme primary)
 * @param {string} [props.type] - Button type (button, submit, reset)
 * @param {React.ReactNode} [props.startIcon] - Icon to display before button text
 * @param {React.ReactNode} [props.endIcon] - Icon to display after button text
 * @returns {JSX.Element} Styled button component
 */
function StyledButton({
  variant = 'contained',
  size = 'medium',
  children,
  onClick,
  sx = {},
  fullWidth = false,
  color,
  type = 'button',
  startIcon,
  endIcon,
  ...props
}) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark;
  const secondary = theme.palette.secondary.main;
  const resolvedColor = color || primary;

  // Ghost and text variants - NO outline, secondary text on hover
  if (variant === 'ghost' || variant === 'text') {
    return (
      <Button
        variant="text"
        size={size}
        onClick={onClick}
        startIcon={startIcon}
        endIcon={endIcon}
        fullWidth={fullWidth}
        type={type}
        sx={{
          color: primary,
          fontWeight: 600,
          textTransform: 'none',
          px: 2,
          py: 1.25,
          borderRadius: '9999px',
          border: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: 'transparent',
            color: secondary,
            boxShadow: 'none',
          },
          '&:focus': {
            boxShadow: 'none',
          },
          ...sx,
        }}
        {...props}
      >
        {children}
      </Button>
    );
  }

  // Link variant - for navigation links styled as buttons
  if (variant === 'link') {
    return (
      <Button
        variant="text"
        size={size}
        onClick={onClick}
        startIcon={startIcon}
        endIcon={endIcon}
        fullWidth={fullWidth}
        type={type}
        sx={{
          color: primary,
          fontWeight: 600,
          textTransform: 'none',
          px: startIcon || endIcon ? 2 : 1.5,
          py: 1.25,
          borderRadius: '9999px',
          border: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          minWidth: 'auto',
          '&:hover': {
            backgroundColor: 'transparent',
            color: secondary,
            textDecoration: 'none',
            boxShadow: 'none',
          },
          ...sx,
        }}
        {...props}
      >
        {children}
      </Button>
    );
  }

  const baseStyles = {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '9999px',
    transition: 'all 0.2s ease',
  };

  const variantStyles = {
    contained: {
      backgroundColor: resolvedColor,
      color: 'white',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
      '&:hover': {
        backgroundColor:
          resolvedColor === primary ? primaryDark : `${resolvedColor}dd`,
        transform: 'translateY(-1px)',
        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
      },
    },
    outlined: {
      backgroundColor: 'transparent',
      color: primary,
      border: `3px solid ${secondary}`,
      boxShadow: 'none',
      '&:hover': {
        borderColor: secondary,
        backgroundColor: `${secondary}1a`,
        transform: 'translateY(-1px)',
      },
    },
  };

  const sizeStyles = {
    small: {
      py: 1,
      px: 2,
      fontSize: '0.875rem',
    },
    medium: {
      py: 1.5,
      px: 3,
      fontSize: '1rem',
    },
    large: {
      py: 2,
      px: 4,
      fontSize: '1.125rem',
    },
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={onClick}
      fullWidth={fullWidth}
      type={type}
      startIcon={startIcon}
      endIcon={endIcon}
      sx={{
        ...baseStyles,
        ...variantStyles[variant],
        ...sizeStyles[size],
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

export default StyledButton;
